/**
 * Portal route paths, in one place so a link and the route it targets can't drift.
 *
 * Two forms, because they are genuinely different strings:
 *
 * - `pattern` is what `<Route path=…>` takes. Relative, with `:params`, because the
 *   portal routes are declared inside a nested `<Route>`.
 * - `to` builds the absolute path a `<Link>` or `navigate()` needs, with its
 *   segments encoded.
 *
 * Keeping the pair together is the point: a route renamed in `ClientApp` without its
 * links updated is the failure this prevents, and that can only be caught if both
 * read from the same entry.
 *
 * Not yet exhaustive — paths are moved here as they're touched. Links elsewhere in
 * the portal still hardcode their strings.
 */

export const ROUTES = {
  groups: {
    pattern: 'groups',
    to: () => '/groups',
  },

  memberSearch: {
    pattern: 'member-search',
    to: () => '/member-search',
  },

  /** A CBS member, keyed by VBA subscriber id. */
  member: {
    pattern: 'member/:id',
    to: (subscriberId: string) => `/member/${encodeURIComponent(subscriberId)}`,
  },

  /**
   * A member administered by an external TPA. Read-only, and keyed by that
   * administrator's own member id rather than a VBA subscriber id — these members
   * have no VBA record.
   */
  externalTpaMember: {
    pattern: 'member/external-tpa/:memberId',
    to: (memberId: string) => `/member/external-tpa/${encodeURIComponent(memberId)}`,
  },

  /**
   * A group administered by an external TPA. Keyed by administrator + group id
   * because a group id is only unique within one administrator; the tenant comes
   * from the session, never the URL.
   */
  externalTpaGroup: {
    pattern: 'groups/external-tpa/:externalTpa/:groupId',
    to: (externalTpa: string, groupId: string) =>
      `/groups/external-tpa/${encodeURIComponent(externalTpa)}/${encodeURIComponent(groupId)}`,
  },
} as const;
